import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, useTheme } from '@mui/material';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useOrderDetails } from 'components/context/order_details';
import { useUserJourneys } from 'components/context/user_journeys';
import ImageBanner, {
  ImageOptions,
} from 'components/common/banners/image_banner';
import PageLoader from 'components/common/page_loader';
import ShippingLabels from 'components/common/shipping_labels';
import SmsSignup from 'components/common/sms_signup';
import TopLevelSummary from 'components/common/summary/top_level_summary';
import AddressSummary from 'components/common/summary/address_summary';
import PaymentSummary from 'components/common/summary/payment_summary';
import ContactSummary from 'components/common/summary/contact_summary';
import ShippingMethodSummary from 'components/common/summary/shipping_method_summary';
import { SMS, isFeatureEnabled } from 'utilities/features';
import { updateCartCount } from 'utilities/nav';
import {
  onCheckoutPageView,
  onCheckoutComplete,
  onCheckoutPageViewV2,
} from 'components/pages/checkout/utilities';
import { PHONE_REGEX_BY_COUNTRY } from 'constants/patterns';
import { isDashboardMode } from 'helpers/search_parameter_helpers';
import DashboardModeFooter from 'components/common/dashboard_mode_footer';
import { shopFooterDisclaimers } from 'utilities/disclaimers';
import { COMPONENT_NAMES } from 'constants/tracking';
import { FrustrationSignal, UserJourneyName } from 'constants/user_journeys';
import { DASHBOARD_SUBPATHS, buildDashboardLink } from 'utilities/links';

const Complete = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { orderRefresh, checkoutClient } = useCheckoutFlow();
  const { number, store, trackingData } = useOrderDetails();
  const { userJourneyTracker } = useUserJourneys();

  const [orderData, setOrderData] = useState({});

  const trackingDataWithIssuerInstallments = (theOrderData = undefined) => {
    const installmentsSelected =
      theOrderData?.issuer_installments_summary?.number_of_payments_selected;

    if (!installmentsSelected || !('order' in trackingData)) {
      return trackingData;
    }

    const thisTrackingData = { ...trackingData };
    thisTrackingData.order.issuer_installments_selected = installmentsSelected;

    return thisTrackingData;
  };

  useEffect(() => {
    orderRefresh()
      .then((data) => {
        setOrderData(data);
        onCheckoutPageView(
          'Order Complete',
          trackingDataWithIssuerInstallments(data),
        );
        onCheckoutPageViewV2({
          componentName: COMPONENT_NAMES.APPS.CHECKOUT.ORDER_COMPLETE,
          additionalParameters: trackingData,
        });
        if (data.checking?.idv_required) {
          setTimeout(() => {
            window.location = buildDashboardLink(
              DASHBOARD_SUBPATHS.DEBIT_CARD_SETUP,
            );
          }, 10000);
        }
      })
      .catch(() => {
        userJourneyTracker.addFrustrationSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frustrationSignal: FrustrationSignal.CompleteStepOrderRefreshError,
        });
      });
    updateCartCount(0);
    onCheckoutComplete(trackingData);

    if (userJourneyTracker) {
      userJourneyTracker.completeUserJourneySuccessfully({
        userJourneyName: UserJourneyName.ShopPlaceOrder,
      });
    }
  }, []);

  if (Object.entries(orderData).length === 0) {
    return <PageLoader />;
  }
  const onCheckingClick = () => {
    window.location.href = buildDashboardLink(
      DASHBOARD_SUBPATHS.DEBIT_CARD_SETUP,
    );
  };

  return (
    <>
      {orderData.is_warranty && (
        <Box component="h2" sx={{ mb: 2 }}>
          {t('checkout.complete.header')}
        </Box>
      )}
      {orderData.checking?.idv_required && (
        <div style={{ marginBottom: 24 }}>
          <ImageBanner
            backgroundColor={
              orderData.checking?.idv_verified
                ? theme.color.yellow
                : theme.color.lightBlue
            }
            backgroundColorGradient={orderData.checking?.idv_verified}
            buttonText={t('checkout.checking.cta')}
            headerText={t('checkout.checking.header')}
            imageType={
              orderData.checking?.idv_verified
                ? ImageOptions.FrontBackCard
                : ImageOptions.FrontCard
            }
            onButtonClick={onCheckingClick}
            subtext={
              orderData.checking?.idv_verified
                ? t('checkout.checking.verified_subtext')
                : t('checkout.checking.unverified_subtext')
            }
          />
        </div>
      )}
      {!orderData.checking?.idv_required && <Divider sx={{ mb: 2 }} />}
      <Grid container>
        <TopLevelSummary orderData={orderData} />
      </Grid>
      {orderData.is_warranty && (
        <>
          <Divider sx={{ my: 2 }} />
          <ShippingLabels checkoutClient={checkoutClient} />
        </>
      )}
      {isFeatureEnabled(SMS) && (
        <>
          <Divider sx={{ my: 2 }} />
          <SmsSignup
            orderNumber={number}
            initialValue={orderData.contact_information?.phone}
            validationRegex={PHONE_REGEX_BY_COUNTRY[store]}
            validationFailedMessage={t('error.validation.invalidPhone')}
            buttonText={t('checkout.sms.submit')}
            header={t('checkout.sms.title')}
            description={t('checkout.sms.description')}
            placeholder={t('checkout.sms.placeholder')}
            disclaimer={t('checkout.sms.disclaimer')}
            successMessage={t('checkout.sms.success')}
            failedMessage={t('checkout.sms.error')}
            retry={t('checkout.sms.retry')}
            url="../../api/sms_signup"
          />
        </>
      )}
      <Divider sx={{ my: 2 }} />
      <Grid container rowSpacing={2}>
        <Grid item xs={12} md={7}>
          <h3>{t('checkout.summary.address.shippingTitle')}</h3>
          <AddressSummary address={orderData.shipping_address} />
        </Grid>
        <Grid item xs={12} md={5}>
          <h3>{t('checkout.summary.address.billingTitle')}</h3>
          <AddressSummary address={orderData.billing_address} />
        </Grid>
        {Object.entries(orderData.payment_information).length > 0 && (
          <Grid container rowSpacing={2}>
            <Grid item xs={12} md={7}>
              <PaymentSummary payment={orderData.payment_information} />
            </Grid>
          </Grid>
        )}
        <Grid item xs={12} md={7}>
          <ContactSummary contact={orderData.contact_information} />
        </Grid>
        {!(orderData.is_warranty || orderData.no_ship) && (
          <Grid item xs={12} md={5}>
            <ShippingMethodSummary
              shippingEstimate={orderData.shipping_estimate}
            />
          </Grid>
        )}
      </Grid>
      <Divider sx={{ my: 2 }} />
      {isDashboardMode() && (
        <DashboardModeFooter disclaimers={shopFooterDisclaimers()} />
      )}
    </>
  );
};

export default Complete;
